import axiosInstance from "utils/axios";
import { toast } from "sonner";

// Get all users
export const getAllUsers = async (params = {}) => {
  try {

    const requestBody = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      search: params.search || "", 
      sortColumn: params.sortColumn || "FullName",
      sortOrder: params.sortOrder || "ASC",
    };

    const response = await axiosInstance.post("/api/User/GetAllUsers", requestBody);


    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data to match the table format
      const transformedUsers = userData.map((user) => ({
        id: user.UserId,
        userId: user.UserId,
        name: user.FullName,
        email: user.Email,
        phone: user.PhoneNumber || "N/A",
        role: user.UserRole || user.RoleName || getRoleNameFromType(user.UserType),
        roleId: user.RoleId,
        userType: user.UserType,
        status: user.IsActive,
        isActive: user.IsActive,
        createdOn: user.CreatedOn,
        companyId: user.CompanyId,
        emailVerified: user.EmailVerified,
        phoneVerified: user.PhoneVerified,
        reportTo: user.ReportTo,
        // Add these fields for edit modal and view modal
        DesignationId: user.DesignationId,
        DesignationName: user.DesignationName,
        ReportTo: user.ReportTo,
        ReportToName: user.ReportToName,
        RoleId: user.RoleId,
        RoleName: user.RoleName,
        IsCounsellor: user.IsCounsellor,
        IsActive: user.IsActive,
        FullName: user.FullName,
        Email: user.Email,
        PhoneNumber: user.PhoneNumber,
        CreatedOn: user.CreatedOn,
        avatar: user.ProfileImageUrl || null,
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch users";

    toast.error("Failed to load users", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

// Helper function to get role name from UserType
function getRoleNameFromType(userType) {
  const roleMap = {
    0: "User",
    1: "User",
    2: "Admin",
    3: "Super Admin",
  };
  return roleMap[userType] || "Unknown";
}

// Create a new user
export const createUser = async (userData) => {
  try {

    // Ensure the data matches the new API structure
    const requestBody = {
      fullName: userData.fullName,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      designationId: userData.designationId,
      roleId: userData.roleId,
      reportTo: userData.reportTo || null,
      isCounsellor: userData.isCounsellor || false,
    };

    const response = await axiosInstance.post(
      "/api/User/CreateUser",
      requestBody,
    );


    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("User created successfully", {
        description: "The new user has been added to the system.",
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User created successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to create user");
    }
  } catch (error) {
    console.error("Error creating user:", error);

    // The axios interceptor normalizes the error, so error.message contains the backend message
    const errorMessage =
      error.message || error.response?.data?.message || "Failed to create user";

    toast.error("Failed to create user", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Update user
export const updateUser = async (userId, userData) => {
  try {
    const response = await axiosInstance.put(
      `/api/User/UpdateUser/${userId}`,
      userData,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("User updated successfully");
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update user");
    }
  } catch (error) {
    console.error("Error updating user:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to update user";

    toast.error("Failed to update user", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Delete user
export const deleteUser = async (userId) => {
  try {
    const response = await axiosInstance.delete(
      `/api/User/DeleteUser/${userId}`,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("User deleted successfully");
      return {
        success: true,
        message: response.data.message || "User deleted successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to delete user");
    }
  } catch (error) {
    console.error("Error deleting user:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to delete user";

    toast.error("Failed to delete user", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Toggle user status (activate/deactivate)
export const toggleUserStatus = async (userId, isActive) => {
  try {
    const response = await axiosInstance.patch(
      `/api/User/ToggleUserStatus/${userId}`,
      { isActive },
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success(
        `User ${isActive ? "activated" : "deactivated"} successfully`,
      );
      return {
        success: true,
        message:
          response.data.message ||
          `User ${isActive ? "activated" : "deactivated"} successfully`,
      };
    } else {
      throw new Error(
        response.data.message || "Failed to toggle user status",
      );
    }
  } catch (error) {
    console.error("Error toggling user status:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to toggle user status";

    toast.error("Failed to update user status", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Update user details (sends data as query parameters)
export const updateUserDetails = async (userData) => {
  try {


    const formData = new FormData();

    formData.append("UserId", userData.userId);
    formData.append("FullName", userData.fullName || "");
    formData.append("PhoneNumber", userData.phoneNumber || "");
    formData.append("DesignationId", userData.designationId || "");
    formData.append("RoleId", userData.roleId || "");
    formData.append("ReportTo", userData.reportTo || "");
    formData.append("IsCounseller", userData.isCounsellor ? "true" : "false");

    // Optional profile image
    if (userData.profileImage) {
      formData.append("ProfileImage", userData.profileImage);
    }

    const response = await axiosInstance.post("/api/User/UpdateUserDetails", formData, {
      // Content-Type handled by axios interceptor for FormData
      timeout: 60000, // 1 minute timeout for profile image upload
    });




    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("User updated successfully", {
        description: "The user details have been updated.",
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update user");
    }
  } catch (error) {
    console.error("Error updating user:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to update user";

    toast.error("Failed to update user", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Get users for assigning leads (counselors/users who can be assigned leads)
export const getUsersForAssignLeads = async () => {
  try {

    const response = await axiosInstance.get("/api/User/GetUsersForAssignLeads", {
      showLoader: false,
    });


    // Check if the response indicates failure (case-insensitive)
    if (response.data?.status === "Failure" || response.data?.statusCode >= 400) {
      throw new Error(response.data?.message || "Failed to fetch users for assignment");
    }

    if (
      response.data.statusCode === 200 &&
      response.data.status?.toLowerCase() === "success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data
      const transformedUsers = userData.map((user) => ({
        id: user.UserId,
        userId: user.UserId,
        fullName: user.FullName,
        // For dropdown labels
        value: user.UserId,
        label: user.FullName,
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users for lead assignment:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch users for assignment";

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

// Get all counselors (legacy - for backward compatibility, now uses getUsersForAssignLeads)
export const getAllCounselors = async () => {
  try {

    // Use the new API instead
    const result = await getUsersForAssignLeads();

    if (result.success) {
      // Add email field for backward compatibility (even if empty)
      const transformedCounselors = result.data.map((user) => ({
        ...user,
        email: user.email || "", // Add empty email if not provided
        phoneNumber: user.phoneNumber || "",
        designationId: user.designationId || null,
        roleId: user.roleId || null,
      }));

      return {
        success: true,
        data: transformedCounselors,
        totalRecords: result.totalRecords,
        message: result.message,
      };
    } else {
      throw new Error(result.error || "Failed to fetch counselors");
    }
  } catch (error) {
    console.error("Error fetching counselors:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch counselors";

    toast.error("Failed to load counselors", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

// Get all users with designations (for ReportTo dropdown)
export const getAllUsersWithDesignations = async () => {
  try {

    const response = await axiosInstance.get("/api/User/GetAllUserWithDesignations");


    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data
      const transformedUsers = userData.map((user) => ({
        userId: user.UserId,
        fullName: user.FullName,
        designationId: user.DesignationId,
        designationName: user.DesignationName,
        designationDescription: user.DesignationDescription,
      }));

      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users with designations:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch users";

    toast.error("Failed to load users", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};


export const getAllUsersForApplication = async (params = {}) => {
  try {

    const requestBody = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      search: params.search || "",
      sortColumn: params.sortColumn || "FullName",
      sortOrder: params.sortOrder || "ASC",
      userType: 2,
    };

    const response = await axiosInstance.post("/api/User/GetAllUsers", requestBody);


    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(userData)) {
        throw new Error("Expected user data to be an array");
      }

      // Transform the API data to match the table format
      const transformedUsers = userData.map((user) => ({
        id: user.UserId,
        userId: user.UserId,
        name: user.FullName,
        email: user.Email,
        phone: user.PhoneNumber || "N/A",
        role: user.RoleName,
        roleId: user.RoleId,
        userType: user.UserType,
        companyId: user.CompanyId,
        designationId: user.DesignationId || null,
        designationName: user.DesignationName || null,
        reportTo: user.ReportTo || null,
        reportToName: user.ReportToName || null,
        isAdmin: user.IsAdmin,
        isActive: user.IsActive,
        emailVerified: user.EmailVerified,
        phoneVerified: user.PhoneVerified,
        createdOn: user.CreatedOn,
        avatar: user.ProfileImageUrl || null, // In case API adds profile image later
      }));


      return {
        success: true,
        data: transformedUsers,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch users";

    toast.error("Failed to load users", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};